/* eslint-disable tap/no-raw-text-js */
import requireContext from 'require-context.macro';
import Vue from 'vue';
import Router from 'vue-router';
import { getters, dispatches } from '@/modules/core/app/helpers/store';
import SessionService from '@/modules/core/session/services/SessionService';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import BaseLayout from '@/BaseLayout';
import { PublicPages, WelcomePathName } from '@/modules/core/app/constants/router.constants';
import SegmentService from '@/modules/core/app/services/SegmentService';
import ZendeskService from '@/modules/core/app/services/ZendeskService';
import PusherService from '@/modules/core/app/services/PusherService';
import UuidService from '@/modules/core/app/services/UuidService';
import {
  AppConfig,
  AppModule,
  AppModuleGroup,
  AppFeature,
} from '@/modules/core/app/constants/app.constants';
import {
  loginRoute,
  welcomeRoute,
  HomeRoute,
  onboardingRoute,
  googleLogin,
  remoteLoginRoute,
  resetPasswordRoute,
  supBillingRouter,
  MaiaChatBotRoute,
  AIChatBotRoute,
  MMMRoute,
  ToConfigAgentRoute,
  AdfloOperationsAgentRoute,
  InsightsEngineRoute,
  AdfloOrdersAgentRoute,
} from '@/modules/core/router';

import {
  fullFrameLegacyRouter,
  rightFrameLegacyRouter,
  billingRouter,
  goalsRouteConfig,
} from '@/modules/legacy/router';

import { Env } from '@/env';
import { PseudoRoute, RouteGroup } from '@/modules/core/app/constants/api.constants';
import { DashboardService } from '@/modules/ta/dashboard/services/DashboardService';
import { sheetsRoute } from '@/modules/sheets/router';
import { gdsRoute } from '@/modules/gds/router';
import { DataLoadStatusRoute } from '@/modules/ta/dataloadstatus/dataloadstatus.constants';
import {
  CLASSIC_DASHBOARD_SESSION_KEY,
  DashboardRoute,
} from '@/modules/ta/dashboard/dashboard.constants';
import { ReportStudioRoute } from '@/modules/ta/reportstudio/reportstudio.constants';
import { oauthShareRoutes } from '@/modules/public/oauthshare/oauthshare.router';
import { addStoreModules } from './store';
import VueXHelper from './modules/core/app/helpers/VueXHelper';

const RedirectHomePageComponent = () =>
  import(/* webpackChunkName: "redirect-home-page-router" */ '@/RedirectHomePageRouter');

let routes = [];
let currentPath = '/';
const requireModules = requireContext(
  './modules',
  true,
  /\/(seo|ta|to|core|audience)\/[a-zA-Z]+\/[a-z]+\.router\.js$/
);
requireModules.keys().forEach((modulePath) => {
  const moduleConfig = requireModules(modulePath);
  const currentRoutes = (moduleConfig.default || moduleConfig).router;
  routes = routes.concat(currentRoutes);
});

Vue.use(Router);

const router = new Router({
  // mode: 'history',
  // eslint-disable-next-line no-undef
  base: process.env.BASE_URL ? process.env.BASE_URL.replace(ASSET_DOMAIN, '') : '/',
  routes: [
    {
      path: '',
      component: BaseLayout,
      children: [
        {
          path: '',
          name: 'layout',
          component: () => import(/* webpackChunkName: "layout-vue" */ '@/AppLayout.vue'),
          children: [
            onboardingRoute,
            welcomeRoute,
            HomeRoute,
            MaiaChatBotRoute,
            AIChatBotRoute,
            MMMRoute,
            ToConfigAgentRoute,
            AdfloOperationsAgentRoute,
            InsightsEngineRoute,
            AdfloOrdersAgentRoute,
            remoteLoginRoute,
            ...rightFrameLegacyRouter,
            ...billingRouter,
            ...goalsRouteConfig,
            ...routes,
            ...supBillingRouter,
            ...sheetsRoute,
            ...gdsRoute,
          ],
        },
        loginRoute,
        googleLogin,
        resetPasswordRoute,
        ...oauthShareRoutes,
        ...fullFrameLegacyRouter,
      ],
    },
    {
      path: '*', // page not found, for now, redirect to home page
      component: RedirectHomePageComponent,
    },
  ],
});

function isLoggedIn() {
  return getters.session.isLoggedIn();
}

function isLoggingIn() {
  return getters.session.isLoggingIn();
}

function isLoggingOut() {
  return getters.session.isLoggingOut();
}

function getUserSettings() {
  return getters.session.getUserSettings();
}

function setLoginRedirectPath(path) {
  return path !== '/' && dispatches.session.setLoginRedirectPath(path);
}

function setOAuthParams(params) {
  if (params?.oauth_client_id && params.oauth_redirect_uri) {
    dispatches.oauth.setOAuthParams(params);
  }
}

function updateContextNav() {
  dispatches.nav.toggleShowFilterButton({ value: false });
}

function getIsCallbackEnabled() {
  return getters.callbackroute.getIsCallbackEnabled();
}

function getCallbackRoutes() {
  return getters.callbackroute.getCallbackRoutes();
}

function updateActiveAppNavItem(to, from) {
  if (to.name !== from?.name) {
    dispatches.nav.updateCurrentAppNavItem({ path: to.path });
  }
}

function setIsCallbackEnabled(isCallbackEnabled) {
  dispatches.callbackroute.setIsCallbackEnabled(isCallbackEnabled);
}

function resetCallbackRoutes() {
  dispatches.callbackroute.resetCallbackRoutes();
}

function setCallbackRoutes() {
  if (getIsCallbackEnabled()) {
    setIsCallbackEnabled(false);
  } else if (getCallbackRoutes()) {
    resetCallbackRoutes();
  }
}

function canRedirectOnLoggedIn(to, from, userSettings) {
  return (
    (to.path === '/' && from.path === '/') ||
    (to.path === '/login' && userSettings?.isLoggedIn && !isLoggingOut())
  );
}

async function onEnter(to, from, next) {
  const prevPath = currentPath;
  currentPath = to.path;
  let userSettings = getUserSettings();

  if (userSettings) {
    DocumentUIService.setTitle(userSettings.applicationName);
  }

  setOAuthParams(to.query);

  if (!window.dashExport) {
    if (!isLoggedIn() && !isLoggingIn()) {
      await dispatches.session.updateUserSettings();
      userSettings = getUserSettings();

      if (!PublicPages.includes(to.name)) {
        // updateUserSettings call can update isLoggedIn
        if (!isLoggedIn()) {
          setLoginRedirectPath(to.path);
          return next('/login');
        }
        // Initialize Pusher in background - don't block navigation
        await PusherService.init();
      }
    }
  }

  if (userSettings !== null) {
    if (isLoggedIn() && !Env.isUnitTesting()) {
      UuidService.kill();

      // Parallelize store module loading for better performance
      const storeModulePromises = [];

      // Add TA store (always required)
      storeModulePromises.push(await addStoreModules(VueXHelper.getStore(), AppModuleGroup.TA));

      // Add TapOrders store
      if (
        (userSettings.isModuleAvailable(AppModule.STELLAR) &&
          getters.session.getUser()?.enable_stellar_module) ||
        userSettings.isModuleAvailable(AppModule.GOALS_PRO) ||
        userSettings.isModuleAvailable(AppModule.MARKETPLACE)
      ) {
        storeModulePromises.push(await addStoreModules(VueXHelper.getStore(), AppModuleGroup.TO));
      }

      // Add SEO store
      if (userSettings.isModuleAvailable(AppModule.SEO)) {
        storeModulePromises.push(await addStoreModules(VueXHelper.getStore(), AppModuleGroup.SEO));
      }

      // Add AUDIENCE store
      if (userSettings.isModuleAvailable(AppModule.AUDIENCE)) {
        storeModulePromises.push(
          await addStoreModules(VueXHelper.getStore(), AppModuleGroup.AUDIENCE)
        );
      }

      // Wait for all store modules to load in parallel
      await Promise.all(storeModulePromises);
    }

    updateActiveAppNavItem(to, from);

    if (userSettings?.user && !SessionService.useNewApp()) {
      DocumentUIService.redirectToOldApp();
    } else {
      if (canRedirectOnLoggedIn(to, from, userSettings)) {
        return next(SessionService.getLoginRedirectPath());
      }

      // Maintain any states between route changes
      updateContextNav();

      to.params.isCallbackEnabled = getIsCallbackEnabled();
      setCallbackRoutes();

      // Show/Hide Zendesk Web Widget
      if (!to.path.includes(RouteGroup.BILLING_OVERVIEW)) {
        ZendeskService.hideZendeskWebWidget();
      } else {
        ZendeskService.showZendeskWebWidget();
      }

      if (!userSettings.isSuperAdmin() && `/${RouteGroup.TRANSFORMATIONHUB}` === to.path) {
        return next('/');
      }

      if (
        userSettings.isOnSupBilling &&
        [
          `/${RouteGroup.BILLING}`,
          `/${RouteGroup.BILLING_ADDRESS}`,
          `/${RouteGroup.BILLING_CREDITCARD}`,
        ].includes(to.path)
      ) {
        // Redirect to new ui billing
        return next(`/${RouteGroup.BILLING_OVERVIEW}`);
      }

      // for new ui billing subscription cancelled case
      const shouldRedirectToNewBilling =
        userSettings.isOnSupBilling &&
        userSettings.isInstanceSuspended() &&
        ![
          `/${RouteGroup.BILLING_OVERVIEW}`,
          `/${RouteGroup.BILLING_INVOICES}`,
          `/${RouteGroup.BILLING_BILLINGDETAILS}`,
          `/${RouteGroup.BILLING_MANAGEPLANS}`,
          `/${RouteGroup.BILLING_CUSTOMIZE}`,
          `/${RouteGroup.LOGIN}`,
        ].includes(to.path);

      if (shouldRedirectToNewBilling) {
        return next(`/${RouteGroup.BILLING_OVERVIEW}`);
      }

      const shouldRedirectToOldBilling =
        !userSettings.isOnSupBilling &&
        userSettings.isInstanceSuspended() &&
        ![
          `/${RouteGroup.BILLING}`,
          `/${RouteGroup.BILLING_ADDRESS}`,
          `/${RouteGroup.BILLING_CREDITCARD}`,
          `/${RouteGroup.LOGIN}`,
        ].includes(to.path);

      if (shouldRedirectToOldBilling) {
        return next(`/${RouteGroup.BILLING}`);
      }
      // redirect users to billing page who don't have sheets (or) gds module as both module endpoints are static pages
      if (
        ((!userSettings.isModuleAvailable(AppModule.SHEETS) ||
          !userSettings.user?.enable_sheets_module ||
          !userSettings.isSuperAdmin()) &&
          [`/${PseudoRoute.SHEETS_GETTINGSTARTED}`, `/${PseudoRoute.SHEETS_TEMPLATES}`].includes(
            to.path
          )) ||
        ((!userSettings.isModuleAvailable(AppModule.GDS) ||
          !userSettings.user?.enable_gds_module ||
          !userSettings.isSuperAdmin()) &&
          [`/${PseudoRoute.GDS_GETTINGSTARTED}`].includes(to.path))
      ) {
        return next(`/${RouteGroup.BILLING}`);
      }
      // restrict test drive instances to access dataloadstatus endpoint
      if (
        userSettings.isTestdriveInstance &&
        `/${DataLoadStatusRoute.DATALOADSTATUS}` === to.path
      ) {
        return next('/');
      }

      // Restrict freemium users to access welcome and onboarding pages through URL
      if (
        userSettings.hasTapReportsFreemiumPlatformSourceLimitation &&
        [welcomeRoute.path, onboardingRoute.path].includes(to.path)
      ) {
        return next(from.path);
      }

      // Restrict free trial users to access welcome page through URL
      if (userSettings.isFreeTrial && welcomeRoute.path === to.path) {
        return next(from.path);
      }

      if (
        to.path.startsWith('/client/iotool') &&
        userSettings.isIOAuthorized &&
        userSettings.isModuleAvailable(AppModule.IO_TOOL)
      ) {
        if (prevPath === '/') {
          return next(WelcomePathName);
        }
        DocumentUIService.navigateTo(to.fullPath);
        return;
      }

      // Restrict new dashboard access
      const hasNewDashboardsModuleAvailable = userSettings.isModuleAvailable(
        AppModule.NEW_DASHBOARD_ACCESS
      );
      const hasNewDashboardsNonSAModuleAvailable = userSettings.isModuleAvailable(
        AppModule.NEW_DASHBOARD_ACCESS_NON_SA
      );
      const hasDisableAngularDashboardsModuleAvailable = userSettings.isModuleAvailable(
        AppModule.DISABLE_ANGULAR_DASHBOARDS
      );
      const hasDefaultNewDashboardExperienceModuleAvailable = userSettings.isModuleAvailable(
        AppModule.DEFAULT_NEW_DASHBOARD_EXPERIENCE
      );

      const isNavigatingToAuditLogs = to.path.includes(`/${RouteGroup.AUDITLOGS}`);
      const isNavigatingToTemplates = to.path.includes(`/${DashboardRoute.DASHBOARDS}/templates`);
      const isNavigatingToLegacyDashboard = to.path.includes(`/${RouteGroup.DASH}/`);
      const isNavigatingFromLegacyDashboard = from.path.includes(`/${RouteGroup.DASH}/`);
      const isNavigatingToNewDashboard = to.path.includes(`/${DashboardRoute.DASHBOARDS}/`);

      // Block legacy dashboard routes if dashboard_view_management feature is not available
      // (New dashboard routes are handled by DashboardRouteConfig in dashboard.router.js)
      if (isNavigatingToLegacyDashboard) {
        if (!userSettings.isFeatureAvailable(AppFeature.DASHBOARD_MANAGEMENT)) {
          if (from && from.name) {
            return next(false);
          }
          return next(SessionService.getLoginRedirectPath());
        }
      }

      // If legacy → legacy, always clear the session key
      if (isNavigatingToLegacyDashboard && isNavigatingFromLegacyDashboard) {
        sessionStorage.removeItem(CLASSIC_DASHBOARD_SESSION_KEY);
      }

      const isSuperAdmin = userSettings.isSuperAdmin();

      /**
       * Permission model
       */
      const canAccessNewDashboard =
        hasNewDashboardsNonSAModuleAvailable || (isSuperAdmin && hasNewDashboardsModuleAvailable);

      const canUseLegacyDashboard =
        !hasNewDashboardsModuleAvailable ||
        (!hasNewDashboardsNonSAModuleAvailable && !isSuperAdmin);

      /**
       * Default experience logic
       */
      const shouldForceNewDashboard =
        (!sessionStorage.getItem(CLASSIC_DASHBOARD_SESSION_KEY) || !from.path.includes('/dash')) &&
        hasDefaultNewDashboardExperienceModuleAvailable &&
        hasNewDashboardsModuleAvailable &&
        hasNewDashboardsNonSAModuleAvailable;

      const shouldUseNewDashboard =
        !canUseLegacyDashboard &&
        (shouldForceNewDashboard || userSettings?.user?.can_access_new_dashboards);

      /**
       * Sync session state
       */
      if (isNavigatingToNewDashboard || isNavigatingToLegacyDashboard) {
        if (userSettings?.user?.can_access_new_dashboards !== shouldUseNewDashboard) {
          dispatches.session.updateCanAccessNewDashboards(shouldUseNewDashboard);
        }
      }

      if (!isNavigatingToAuditLogs && !isNavigatingToTemplates) {
        const dashboardIdFromLegacy = isNavigatingToLegacyDashboard
          ? to.path.split(`/${RouteGroup.DASH}/`)[1]
          : null;

        const dashboardIdFromNew = isNavigatingToNewDashboard
          ? to.path.split(`/${DashboardRoute.DASHBOARDS}/`)[1]
          : null;

        const dashboardId = dashboardIdFromLegacy || dashboardIdFromNew;

        /**
         * Leads dashboard handling (Audit Logs dependency)
         */
        if (dashboardId && dashboardId !== RouteGroup.LEADS) {
          try {
            const dashboardService = new DashboardService();
            const isLeadsDashboard = await dashboardService.isLeadsDashboard(dashboardId);

            if (isLeadsDashboard) {
              const targetPath = `/${RouteGroup.LEADS}`;
              if (to.path !== targetPath) return next(targetPath);
            }
          } catch (e) {
            console.error('[Router] Failed to fetch dashboard:', e);
          }
        }

        /**
         * Disable Angular dashboards → always force new
         */
        if (hasDisableAngularDashboardsModuleAvailable) {
          if (isNavigatingToLegacyDashboard) {
            const isToFav = to.path.includes('/fav/');
            const targetPath = isToFav
              ? `/${DashboardRoute.FAV_DASHBOARDS}/${dashboardId}`
              : `/${DashboardRoute.DASHBOARDS}/${dashboardId}`;
            return next(targetPath);
          }
          return next();
        }

        /**
         * Legacy → New
         */
        if (canAccessNewDashboard && shouldUseNewDashboard && isNavigatingToLegacyDashboard) {
          sessionStorage.removeItem(CLASSIC_DASHBOARD_SESSION_KEY);

          const isFromFav = from.path.includes('/fav/');
          const isToFav = to.path.includes('/fav/');
          const targetPath =
            isFromFav || isToFav
              ? `/${DashboardRoute.FAV_DASHBOARDS}/${dashboardIdFromLegacy}`
              : `/${DashboardRoute.DASHBOARDS}/${dashboardIdFromLegacy}`;

          if (to.path !== targetPath) return next(targetPath);
        }

        /**
         * New → Legacy (not allowed)
         */
        if ((!canAccessNewDashboard || !shouldUseNewDashboard) && isNavigatingToNewDashboard) {
          const isFromFav = from.path.includes('/fav/');
          const isToFav = to.path.includes('/fav/');
          const targetPath =
            isFromFav || isToFav
              ? `/fav/${RouteGroup.DASH}/${dashboardIdFromNew}`
              : `/${RouteGroup.DASH}/${dashboardIdFromNew}`;

          if (to.path !== targetPath) return next(targetPath);
        }
      }

      // Restrict new report studio access
      const hasNewReportStudioModuleAvailable = userSettings.isModuleAvailable(
        AppModule.NEW_REPORT_STUDIO_ACCESS
      );
      const hasDisableAngularReportStudioModuleAvailable = userSettings.isModuleAvailable(
        AppModule.DISABLE_ANGULAR_REPORT_STUDIO
      );
      const hasDefaultNewReportStudioExperienceModuleAvailable = userSettings.isModuleAvailable(
        AppModule.DEFAULT_NEW_REPORT_STUDIO_EXPERIENCE
      );
      const shouldRedirectToNewReportStudio = userSettings?.user?.can_access_new_report_studio;

      const isNavigatingToNewReportStudio = to.path.includes(`/nui/${RouteGroup.REPORTSTUDIO}/`);
      const isNavigatingToLegacyReportStudio =
        to.path.includes(`/${RouteGroup.REPORTSTUDIO}/`) && !isNavigatingToNewReportStudio;

      if (!userSettings.isRemoteLogin) {
        const reportIdFromLegacy = to.path.includes(`/${ReportStudioRoute.REPORTSTUDIO}/`)
          ? to.path.split(`/${ReportStudioRoute.REPORTSTUDIO}/`)[1]
          : null;
        const reportIdFromNew = to.path.includes(`/${ReportStudioRoute.NUI_REPORTSTUDIO}/`)
          ? to.path.split(`/${ReportStudioRoute.NUI_REPORTSTUDIO}/`)[1]
          : null;

        const reportId = reportIdFromLegacy || reportIdFromNew;

        // Disable Angular report studio → always force new
        if (hasDisableAngularReportStudioModuleAvailable) {
          if (isNavigatingToLegacyReportStudio) {
            const isToFav = to.path.includes('/fav/');
            const targetPath = isToFav
              ? `/${ReportStudioRoute.FAV_NUI_REPORTSTUDIO}/${reportId}`
              : `/${ReportStudioRoute.NUI_REPORTSTUDIO}/${reportId}`;
            return next(targetPath);
          }
          return next();
        }

        if (
          (isNavigatingToNewReportStudio || isNavigatingToLegacyReportStudio) &&
          hasDefaultNewReportStudioExperienceModuleAvailable
        ) {
          return next();
        }

        // Redirect Legacy → New (if allowed)
        if (
          hasNewReportStudioModuleAvailable &&
          shouldRedirectToNewReportStudio &&
          isNavigatingToLegacyReportStudio
        ) {
          const isFromFav = from.path.includes('/fav/');
          const targetPath = isFromFav
            ? `/${ReportStudioRoute.FAV_NUI_REPORTSTUDIO}/${reportId}`
            : `/${ReportStudioRoute.NUI_REPORTSTUDIO}/${reportId}`;

          if (to.path !== targetPath) return next(targetPath);
        }

        // Redirect New → Legacy (only if NOT allowed and on new path)
        if (
          isNavigatingToNewReportStudio &&
          (!hasNewReportStudioModuleAvailable || !shouldRedirectToNewReportStudio)
        ) {
          const isFromFav = from.path.includes('/fav/');
          const targetPath = isFromFav
            ? `/${ReportStudioRoute.FAV_REPORTSTUDIO}/${reportId}`
            : `/${ReportStudioRoute.REPORTSTUDIO}/${reportId}`;
          if (to.path !== targetPath) return next(targetPath);
        }
      }

      return next();
    }
  } else {
    return next();
  }
}

router.beforeEach(onEnter);

router.afterEach((to, from) => {
  if (!Env.isUnitTesting() && !window.dashExport) {
    const referrer =
      window.referrer || `https://${window.location.host}${AppConfig.NUI_PATH}${from.path}`;
    const path = `https://${window.location.host}${AppConfig.NUI_PATH}${to.path}`;
    SegmentService.page(to.name, path, referrer);
  }
  dispatches.guide.resetLoaders();
});

export default router;
